import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { QueryList } from '@angular/core';

import { GasSelectComponent } from '../../forms/gas-select/wrapper/gas-select.component';
import { GasInputComponent } from '../../forms/gas-input/input/gas-input.component';
import { GasFormKeyDirective } from '../../utils/gas-form-key/gas-form-key.directive';
import { GasPaginator } from '../gas-paginator/gas-paginator.namespace';
import { GasRadioGroupComponent } from '../../forms/gas-radio/group/gas-radio-group.component';
import { GasCheckboxComponent } from '../../forms/gas-checkbox/checkbox/gas-checkbox.component';
import { GasRadioComponent } from '../../forms/gas-radio/radio/gas-radio.component';
import { GasSwitchComponent } from '../../forms/gas-switch/switch/gas-switch.component';
import { GasCheckbox } from '../../forms/gas-checkbox/gas-checkbox.namespace';
import { GasSelectOptionComponent } from '../../forms/gas-select/option/gas-select-option.component';
import { GasTags } from '../gas-tags/gas-tags.namespace';
import { GasDatepickerInputComponent } from '../../forms/gas-datepicker-input/gas-datepicker-input.component';
import { GasAutocompleteComponent } from '../../forms/gas-autocomplete/wrapper/gas-autocomplete.component';
import { GasStatusTagGroupComponent } from '../../forms/gas-status-tag/group/group.component';

export namespace GasTable {
  export enum TableRowsSelectionMode {
    None = 'none',
    Single = 'single',
    Multiple = 'multiple',
    OnlyBody = 'onlyBody',
  }

  export enum TableSorting {
    Asc = 'asc',
    Desc = 'desc',
  }

  export interface TableParams {
    filter?: GasTable.FilterValue;
    pagination?: GasPaginator.Data;
    sorting?: GasTable.SortingValue;
  }

  export type TableValue<T = unknown> = Record<string, T>;

  export type TableSelectedValue<T = unknown> = TableValue<T> | TableValue<T>[];

  export type AdvancedFilterValue<T = unknown> = Record<string, T>;

  export type FilterValue = AdvancedFilterValue | string;

  export type SortingValueType = 'asc' | 'desc';

  export type SortingValue = Record<string, SortingValueType> | undefined;

  export interface Action {
    icon: string;
    callback: () => void;
    tooltip: string;
    loading$?: Observable<boolean>;
    disabled$?: Observable<boolean>;
  }

  export interface TableColumnVisibilitySettings extends GasCheckbox.Checkbox {
    index: number;
  }

  export interface Texts {
    tag?: { filter?: string; resetAll?: string };
    empty?: { resetFilters: string };
    error?: { tryReload?: string; reloadButton?: string };
    filter?: { cancel?: string; reset?: string; apply?: string };
  }

  export interface FilterButtonCustomFunctions {
    cancelCallback?: () => void;
    resetCallback?: () => void;
    applyCallback?: () => void;
  }

  export const DEFAULT_TEXTS: Texts = {
    tag: {
      filter: 'Фильтр:',
      resetAll: 'Сбросить все',
    },
    empty: {
      resetFilters: 'Сбросить фильтры',
    },
    error: {
      tryReload: 'Попробуйте перезагрузить страницу',
      reloadButton: 'Перезагрузить',
    },
    filter: {
      cancel: 'Отменить текущие изменения',
      reset: 'Сбросить все фильтры',
      apply: 'Применить',
    },
  };

  /** Генерируем текст тега */
  export function generateTag(formKeyDirectives: GasFormKeyDirective[]): void {
    if (formKeyDirectives?.length === 1) {
      const component = formKeyDirectives[0].component;

      switch (true) {
        case component instanceof GasInputComponent: {
          const inputComponent = component as GasInputComponent;
          let date = '';

          /** Если в value лежит дата, то необходимо преобразовать ее в читаемый формат для отображениия пользователю */
          if (inputComponent.form.value instanceof Date) {
            const selectDate = new Date(inputComponent.form.value);
            date = this.getDateForLabel(selectDate);
          }

          formKeyDirectives[0].tagName = `${inputComponent.label.replace(':', '')}: ${date || inputComponent.form.value}`;
          break;
        }

        case component instanceof GasSelectComponent: {
          const selectComponent = component as GasSelectComponent;

          // Если данные загружены
          if (!!selectComponent.options?.length) {

            this.setGasSelectTitle(selectComponent, selectComponent.options, formKeyDirectives[0]);

          } else {
            // Если данные ещё не подгрузились, сделаем подписку
            formKeyDirectives[0].tagName = `${selectComponent.label.replace(':', '')}: Loading`;

            selectComponent.options.changes
              .pipe(first())
              .subscribe((options) => {
                this.setGasSelectTitle(selectComponent, options, formKeyDirectives[0]);
              });
          }
          break;
        }

        case component instanceof GasRadioGroupComponent: {
          const radioGroupComponent = component as GasRadioGroupComponent;
          const value = radioGroupComponent.form.value;
          const textValue = radioGroupComponent.optionRefs.find((radio: GasRadioComponent) => radio.value === value)?.label;

          formKeyDirectives[0].tagName = `${radioGroupComponent.label.toString().replace(':', '')}: ${textValue}`;

          break;
        }

        case component instanceof GasCheckboxComponent: {
          const checkboxComponent = component as GasCheckboxComponent;
          const valueText = checkboxComponent.form.value === true
            ? 'Включено'
            : 'Выключено';

          formKeyDirectives[0].tagName = `${checkboxComponent.elementRef.nativeElement.textContent.replace(':', '')}: ${valueText}`;

          break;
        }

        case component instanceof GasSwitchComponent: {
          const checkboxComponent = component as GasSwitchComponent;
          const element: HTMLElement = checkboxComponent.elementRef.nativeElement;
          const valueText = checkboxComponent.form.value === true
            ? 'Включено'
            : 'Выключено';

          formKeyDirectives[0].tagName = `${element.querySelector('.switch_right-title').textContent.replace(':', '')}: ${valueText}`;

          break;
        }

        case component instanceof GasDatepickerInputComponent: {
          const datepickerInputComponent = component as GasDatepickerInputComponent;

          const date = getDateFrom(datepickerInputComponent.form.value);

          if (datepickerInputComponent.combine) {
            if (datepickerInputComponent.combinePos === 'first') {
              formKeyDirectives[0].tagName = `${datepickerInputComponent.label} с ${date || datepickerInputComponent.form.value}`;
              break;
            }

            if (datepickerInputComponent.combinePos === 'last') {
              formKeyDirectives[0].tagName = `${datepickerInputComponent.label} по ${date || datepickerInputComponent.form.value}`;
              break;
            }
          }

          formKeyDirectives[0].tagName =
            `${datepickerInputComponent.label.replace(':', '')}: ${date || datepickerInputComponent.form.value}`;
          break;
        }

        case component instanceof GasAutocompleteComponent: {
          const autocompleteComponent = component as GasAutocompleteComponent;
          formKeyDirectives[0].tagName = `${autocompleteComponent.label}: ${autocompleteComponent?.form?.value?.title}`;
          break;
        }

        case component instanceof GasStatusTagGroupComponent: {
          const statusGroupComponent = component as GasStatusTagGroupComponent;
          const optionsLabels = statusGroupComponent.gasStatusTagItems
            .filter(item => (statusGroupComponent.form?.value || []).includes(item.value))
            .map(item => item.label);
          const value = optionsLabels.join(', ');
          const label = statusGroupComponent.label;
          formKeyDirectives[0].tagName = `${label}: ${value}`;
          break;
        }

        default:
          formKeyDirectives[0].tagName = '';
      }
    } else {
      const firstComponent = formKeyDirectives
        .find((directive: GasFormKeyDirective) => directive.component.combinePos === 'first').component;

      const secondComponent = formKeyDirectives
        .find((directive: GasFormKeyDirective) => directive.component.combinePos === 'last').component;

      if (!firstComponent || !secondComponent) {
        return;
      }

      const inputFirstComponent = firstComponent as GasInputComponent;
      const inputSecondComponent = secondComponent as GasInputComponent;

      const dateFrom = getDateFrom(firstComponent.form.value);
      const dateTo = getDateFrom(secondComponent.form.value);

      formKeyDirectives[0].tagName = `
        ${inputFirstComponent.label} с ${dateFrom || inputFirstComponent.form.value} по ${dateTo || inputSecondComponent.form.value}
      `;
    }
  }

  /** Генерируем тэги фильтров */
  export function generateTags(directives: GasFormKeyDirective[]): GasTags.GasTag<GasFormKeyDirective[]>[] {
    directives = directives.filter((directive: GasFormKeyDirective) => {
      if (Array.isArray(directive.component?.form.value)) {
        return directive.component.form.value?.length ? directive : undefined;
      } else {
        return (directive.component?.form.value != null && directive.component?.form.value !== '')
        || directive.component instanceof GasSwitchComponent
          ? directive : undefined;
      }
    }).filter((directive: GasFormKeyDirective) => !!directive);

    const resultTags = GasTable.getCombinedInputs(directives);
    resultTags.forEach((directive: GasFormKeyDirective[]) => GasTable.generateTag(directive));

    return resultTags.map((directive: GasFormKeyDirective[]) =>
      ({label: directive[0].tagName, value: directive} as GasTags.GasTag<GasFormKeyDirective[]>));
  }

  /** Группируем модифицированные инпуты по combine признаку. Это нужно в случае, если у нас используется интервал */
  export function getCombinedInputs(directives: GasFormKeyDirective[]): GasFormKeyDirective[][] {
    const result: GasFormKeyDirective[][] = [];

    directives?.forEach((directive: GasFormKeyDirective) => {
      if (!directive.component.combine) {
        result.push([directive]);
      } else {
        const alreadyHasCombineInResult = result.find((innerResult: GasFormKeyDirective[]) => {
          return innerResult.find((res: GasFormKeyDirective) => res.component.combine === directive.component.combine) !== undefined;
        }) !== undefined;

        if (alreadyHasCombineInResult) {
          return;
        }

        const combinedInputs = directives.filter((innerDirective: GasFormKeyDirective) => {
          return innerDirective.component.combine === directive.component.combine;
        });

        /** Если у нас только один инпут с key combine, тогда пушим как обычно */
        if (combinedInputs.length === 1) {
          result.push([directive]);
        } else {
          result.push(combinedInputs);
        }
      }
    });

    return result;
  }

  export function getDateFrom(value: string | Date): string {
    if (value instanceof Date) {
      return getDateForLabel(new Date(value));
    }

    return '';
  }

  export function getDateForLabel(selectDate: Date): string {
    const date = `
      ${addZero(selectDate.getDate())}.${addZero(selectDate.getMonth() + 1)}.${selectDate.getFullYear()}
    `;

    return date;
  }

  export function addZero(date: number): string | number {
    return date < 10 ? '0' + date : date;
  }

  export function setGasSelectTitle(selectComponent: GasSelectComponent, options: QueryList<GasSelectOptionComponent>,
    formKeyDirective: GasFormKeyDirective): void {
    if (selectComponent.multi) {
      const valueArr = selectComponent.form.value;

      if (!valueArr?.length) {
        return;
      }

      const resultTitle: string[] = [];

      options?.forEach((option: GasSelectOptionComponent) => {
        valueArr?.forEach((value: any) => {
          if (option.value === value) {
            resultTitle.push(option.title.trim());
          }
        });
      });

      formKeyDirective.tagName = `${selectComponent.label.replace(':', '')}: ${resultTitle.join(',')}`;
    } else {

      const resultTitle = options
        .find((option: GasSelectOptionComponent) => option.value === selectComponent.form.value)?.title || 'No title';

      formKeyDirective.tagName = `${selectComponent.label.replace(':', '')}: ${resultTitle}`;
    }
  }
}
